import React from 'react';
import Icon from '../Text/Icon';
import Subheading from '../Text/Subheading';
import Text from '../Text/Text';
import HorizontalDivider from '../Dividers/HorizontalDivider';

/**
 * KeyboardShortcutsModal - Display all available keyboard shortcuts
 */
export default function KeyboardShortcutsModal({isOpen,onClose}) {
    if(!isOpen) return null;

    const isMac=navigator.userAgent.toUpperCase().includes('MAC');
    const modKey=isMac? '⌘':'Ctrl';

    const shortcuts=[
        {
            category: 'Selection',
            items: [
                {keys: `${modKey} + A`,description: 'Select all nodes'},
                {keys: 'Shift + Drag',description: 'Box selection (drag on empty canvas)'},
                {keys: `${modKey} + Click`,description: 'Add/remove nodes from selection'},
            ]
        },
        {
            category: 'Editing',
            items: [
                {keys: 'Delete / Backspace',description: 'Delete selected elements'},
                {keys: `${modKey} + C`,description: 'Copy selected nodes'},
                {keys: `${modKey} + V`,description: 'Paste nodes'},
                {keys: `${modKey} + D`,description: 'Duplicate selected nodes'},
            ]
        },
        {
            category: 'Navigation',
            items: [
                {keys: 'Drag Empty Canvas',description: 'Pan/move the canvas view'},
                {keys: 'Arrow Keys',description: 'Fine-tune node position (10px)'},
                {keys: 'Mouse Wheel',description: 'Zoom in/out'},
                {keys: '+ / =',description: 'Zoom in'},
                {keys: '-',description: 'Zoom out'},
            ]
        },
        {
            category: 'Text Formatting',
            items: [
                {keys: `${modKey} + B`,description: 'Bold text (in nodes)'},
                {keys: `${modKey} + I`,description: 'Italic text (in nodes)'},
            ]
        },
        {
            category: 'AI Assistant',
            items: [
                {keys: `${modKey} + K`,description: 'Toggle AI chat panel'},
                {keys: `${modKey} + 1`,description: 'Insert /complete command'},
                {keys: `${modKey} + 2`,description: 'Insert /optimize command'},
                {keys: `${modKey} + 3`,description: 'Insert /rearrange command'},
                {keys: `${modKey} + 4`,description: 'Insert /generate command'},
                {keys: `${modKey} + 5`,description: 'Insert /modify command'},
                {keys: `${modKey} + 6`,description: 'Insert /delete command'},
                {keys: 'Esc',description: 'Close AI chat or modals'},
            ]
        },
        {
            category: 'Help',
            items: [
                {keys: '?',description: 'Show this help dialog'},
            ]
        }
    ];

    return (
        <div
            className='flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50'
            onClick={onClose}
        >
            <div
                className='bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto m-4'
                onClick={(e) => e.stopPropagation()}
            >
                <div className='flex sticky top-0 justify-between items-center p-4 bg-white border-b'>
                    <Subheading className='!mb-0'>Keyboard Shortcuts</Subheading>
                    <button
                        onClick={onClose}
                        className='p-2 rounded-full transition-colors hover:bg-gray-100'
                        aria-label='Close'
                    >
                        <Icon icon='x' className='text-xl' />
                    </button>
                </div>

                <div className='p-6 space-y-6'>
                    {shortcuts.map((section,index) => (
                        <div key={section.category}>
                            <Text className='mb-3 text-sm font-semibold text-gray-500 uppercase'>
                                {section.category}
                            </Text>
                            <div className='space-y-2'>
                                {section.items.map((shortcut,idx) => (
                                    <div
                                        key={idx}
                                        className='flex justify-between items-center px-2 py-2 rounded hover:bg-gray-50'
                                    >
                                        <Text className='text-sm text-gray-700'>
                                            {shortcut.description}
                                        </Text>
                                        <kbd className='px-3 py-1.5 text-sm font-semibold text-gray-800 bg-gray-100 rounded border border-gray-300 shadow-sm'>
                                            {shortcut.keys}
                                        </kbd>
                                    </div>
                                ))}
                            </div>
                            {index<shortcuts.length-1&&<HorizontalDivider />}
                        </div>
                    ))}
                </div>

                <div className='sticky bottom-0 p-4 bg-gray-50 border-t'>
                    <Text className='text-xs text-center text-gray-500'>
                        Press <kbd className='px-2 py-1 text-xs bg-white rounded border'>?</kbd> anytime to view shortcuts
                    </Text>
                </div>
            </div>
        </div>
    );
}
